import { io, Socket } from 'socket.io-client';
import store from '@/store';
import { CHAT_SOCKET_EVENT, SYNC_ACCOUNT_ORGANIZATION } from '@/store/action_types';
import { PlatformModule } from '@/utils/enums';

/**
 * One socket for the signed-in session. The API pushes chat events (new messages,
 * bell entries, presence, typing, read receipts) and the store applies them; every
 * change the user makes still goes through the REST API.
 */

const EVENTS = [
  'message:new',
  'message:deleted',
  'conversation:updated',
  'conversation:added',
  'conversation:removed',
  'conversation:read',
  'conversation:muted',
  'notification:new',
  'notification:read',
  'notification:seen',
  'presence:list',
  'presence',
  'typing',
];

let socket: Socket | null = null;
let currentToken: string | null = null;

/** The API origin without the `/api` path, where the socket server listens. */
export const chatSocketUrl = (): string => {
  const base = process.env.NODE_ENV == 'development' ? process.env.VUE_APP_DEV_SERVER_URL : process.env.VUE_APP_SERVER_URL;
  try {
    return new URL(String(base)).origin;
  } catch (e) {
    return String(base ?? '').replace(/\/api.*$/, '');
  }
};

export const connectChatSocket = (token: string): void => {
  if (!token) return;
  if (socket && currentToken === token) {
    if (!socket.connected) socket.connect();
    return;
  }
  disconnectChatSocket();
  currentToken = token;

  socket = io(chatSocketUrl(), {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15000,
    timeout: 20000,
  });

  socket.on('connect', () => store.dispatch(CHAT_SOCKET_EVENT, { event: 'connect', payload: {} }));
  socket.on('disconnect', (reason) => {
    store.dispatch(CHAT_SOCKET_EVENT, { event: 'disconnect', payload: { reason } });
    // The server closes a socket on purpose only when the organization's chat module was
    // switched off. Refresh the licence list (App.vue then tears the chat down); if chat
    // turns out to be licensed still, come back, since the client does not reconnect on
    // its own after a server-side close.
    if (reason === 'io server disconnect') {
      store.dispatch(SYNC_ACCOUNT_ORGANIZATION, { force: true }).then(() => {
        if (socket && !socket.connected && store.getters.enabledModules.includes(PlatformModule.chat)) socket.connect();
      });
    }
  });
  socket.on('connect_error', (error) => {
    const message = String(error?.message ?? '');
    // A rejected token means the session is gone; the API client signs the user out on
    // its next call, so here we only stop hammering the server.
    if (/unauthorized/i.test(message)) {
      socket?.disconnect();
      return;
    }
    // The organization is not licensed for chat (the session data was stale): stop
    // reconnecting and refresh the licence list so the chat UI goes away.
    if (/module disabled/i.test(message)) {
      socket?.disconnect();
      store.dispatch(SYNC_ACCOUNT_ORGANIZATION, { force: true });
    }
  });
  for (const event of EVENTS) {
    socket.on(event, (payload: unknown) => store.dispatch(CHAT_SOCKET_EVENT, { event, payload }));
  }
};

export const disconnectChatSocket = (): void => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }
  socket = null;
  currentToken = null;
};

export const emitTyping = (conversationId: string, isTyping: boolean): void => {
  if (socket?.connected) socket.emit('typing', { conversationId, isTyping });
};

export const isChatSocketConnected = (): boolean => !!socket?.connected;

/** Called when the mobile app comes back to the foreground: reconnect right away. */
export const nudgeChatSocket = (): void => {
  if (socket && !socket.connected) socket.connect();
};
